import { addEntity, deleteEntity, dropEntity, isModuleEntity, wrapChildren, wrapEntity } from "@client/lib/layout";
import { traverseObject } from "@client/lib/object";
import {
  collapseResponsiveSettings,
  fromShorthand,
  getSettingValue,
  innerHorizontalAlignToJustifyContent,
  processPossiblyResponsiveValue,
  removeSetting,
  replaceSetting,
  setResponsiveValue,
  setValue,
  toShorthand
} from "@client/lib/settings";
import { parseValue } from "@client/lib/values/square";
import { createEntity } from "@client/settings/entity";
import { createModule } from "@client/settings/module";
import {
  type CompositeSettings,
  type Entities,
  type Entity,
  EntityType,
  type GenericMap,
  type MainSettings,
  type ModuleEntity,
  ScreenType
} from "@client/types";
import { isUndefined } from "lodash";
import { compareVersionNumbers } from ".";

// NOTE: must not use createEntity or createModule, as they create the latest version of the entity. Suggest to hardcode the entity settings for the version

export const version = "04.00.02";
export const upgradeEntities = (entities: Entities, settings: MainSettings | null): Entities => {
  const newContainers: GenericMap<{ paddingMoved: boolean; marginMoved: boolean }> = {};

  Object.values(entities).forEach((entity) => {
    if (!entity.settings || compareVersionNumbers(entity.settings.version, version) >= 0) return;

    if (entity.type !== EntityType.Column) {
      delete entity.dimensionSize;
    }

    switch (entity.type) {
      case EntityType.Block: {
        entity.settings = replaceSetting(entity.settings, "width", "innerWidth");

        entity.settings = setResponsiveValue(entity.settings, "align", "center");
        entity.settings = setResponsiveValue(entity.settings, "height", "hug");
        entity.settings = setResponsiveValue(entity.settings, "gap", "0px");
        entity.settings = setResponsiveValue(entity.settings, "direction", "column");

        processPossiblyResponsiveValue(entity.settings, (x, s) => {
          if (x.minHeight === "auto" || x.minHeight === "0vh") {
            delete x.minHeight;
          }
        });

        break;
      }
      case EntityType.Row: {
        entity.settings = replaceSetting(entity.settings, "stackChildren", "direction", (v) => (v ? "column" : "row"));
        entity.settings = replaceSetting(entity.settings, "reverseChildren", "reverse");
        break;
      }

      case EntityType.Column: {
        entity.settings = setResponsiveValue(entity.settings, "align", "flex-start");
        entity.settings = setResponsiveValue(entity.settings, "direction", "column");
        entity.settings = setResponsiveValue(entity.settings, "gap", "0px");

        break;
      }

      case EntityType.Container: {
        entity.settings = setResponsiveValue(entity.settings, "align", "flex-start");
        entity.settings = setResponsiveValue(entity.settings, "direction", "column");
        entity.settings = setResponsiveValue(entity.settings, "justify", "flex-start");
        break;
      }

      case EntityType.Module: {
        if ((entity as ModuleEntity).moduleType === "Checkout") {
          processPossiblyResponsiveValue(entity.settings, (x, s) => {
            if (isUndefined(x.discountField) && s === ScreenType.Desktop) {
              x.discountField = true;
            }
          });
        }

        if ((entity as ModuleEntity).moduleType === "Image") {
          processPossiblyResponsiveValue(entity.settings, (x, s) => {
            if (x.image?.border?.radius) {
              if (x.image.border.radius.toString().split("|").length === 6) {
                // old format
                const v = parseValue(x.image.border.radius);
                x.image.radius = toShorthand(
                  {
                    top: v.first,
                    right: v.second,
                    bottom: v.third,
                    left: v.fourth
                  },
                  "{top} {right} {bottom} {left}",
                  "0px"
                );
                delete x.image.border.radius;
              }
            }
          });
        }

        break;
      }
    }

    switch (entity.type) {
      case EntityType.Block:
      case EntityType.Column: {
        entity.settings = replaceSetting(entity.settings!, "contentVerticalAlign", "justify", (x) => {
          switch (x) {
            case "top":
              return "flex-start";
            case "bottom":
              return "flex-end";
            default:
              return x;
          }
        });
        break;
      }
    }

    entity.settings = upgradeBackground(entity.settings);
  });

  const getShouldWrap = (id: string, shouldFn: (x: any) => boolean): boolean => {
    let should = false;
    processPossiblyResponsiveValue(entities[id].settings!, (x, s) => {
      if (should) return;

      should ||= shouldFn(x);
    });
    return should;
  };

  const values = Object.values(entities);
  values.forEach((entity) => {
    if (!entity.settings || compareVersionNumbers(entity.settings.version, version) >= 0) return;

    switch (entity.type) {
      case EntityType.Module: {
        let currentId = entity.id;

        const module = entity as ModuleEntity;

        if (module.moduleType === "Button") break;
        if (module.parentId && isModuleEntity(entities[module.parentId])) break; // module inside module

        switch (module.moduleType) {
          case "Icon":
          case "Image":
          case "SocialIcons":
          case "TextHTML":
          case "Title": {
            entity.settings = replaceSetting(entity.settings!, "innerHorizontalAlign", "alignText", (v) => {
              if (module.moduleType === "Image" && v === "justify") return "left";
              return v;
            });
            break;
          }
        }

        if (module.moduleType === "Maps") {
          entity.settings = replaceSetting(entity.settings, "mapHeight", "height");
        }

        let needsWidthWrap = false;

        if (
          getShouldWrap(
            entity.id,
            (x) =>
              (x.padding && x.padding !== "0px") ||
              (x.margin && x.margin !== "0px") ||
              !!x.background ||
              !!x.border ||
              !!x.shadow ||
              (!!x.containerWidth && x.containerWidth !== "100%" && x.containerWidth !== "auto")
          )
        ) {
          const secondContainer = createEntity(entities[currentId].parentId!, EntityType.Container);
          secondContainer.settings = setResponsiveValue(secondContainer.settings!, "padding", "0px");
          entities = wrapEntity(entities, currentId, () => {
            processPossiblyResponsiveValue(entity.settings!, (x, s) => {
              secondContainer.settings = setValue(secondContainer.settings!, "margin", x.margin, s);
              secondContainer.settings = setValue(secondContainer.settings!, "background", x.background, s);
              secondContainer.settings = setValue(secondContainer.settings!, "padding", x.padding, s);
              secondContainer.settings = setValue(secondContainer.settings!, "border", x.border, s);
              secondContainer.settings = setValue(secondContainer.settings!, "shadow", x.shadow, s);

              needsWidthWrap ||= x.containerWidth !== "100%" && x.containerWidth !== "auto";

              if (x.containerWidth === "100%") {
                secondContainer.settings = setValue(secondContainer.settings, "width", "fill", s);
              } else if (x.containerWidth === "auto") {
                secondContainer.settings = setValue(secondContainer.settings, "width", "100%", s);
              } else {
                secondContainer.settings = setValue(secondContainer.settings, "width", x.containerWidth, s);
                secondContainer.settings = setValue(secondContainer.settings, "maxWidth", "100%", s);
              }

              delete x.margin;
              delete x.background;
              delete x.padding;
              delete x.border;
              delete x.shadow;
            });

            return secondContainer;
          });

          newContainers[secondContainer.id] = {
            marginMoved: true,
            paddingMoved: false
          };

          currentId = secondContainer.id;
        }

        entity.settings = replaceSetting(entity.settings, "containerWidth", "width", (x) => "fill");

        if (needsWidthWrap) {
          const thirdContainer = createEntity(entities[currentId].parentId!, EntityType.Container);
          thirdContainer.settings = setResponsiveValue(thirdContainer.settings!, "padding", "0px");
          entities = wrapEntity(entities, currentId, () => {
            processPossiblyResponsiveValue(entity.settings!, (x, s) => {
              const hAlign = x.horizontalAlign;
              if (hAlign) {
                const align = hAlign === "right" ? "flex-end" : hAlign === "center" ? "center" : "flex-start";
                thirdContainer.settings = setValue(thirdContainer.settings!, "align", align, s);
              }
            });
            return thirdContainer;
          });

          newContainers[thirdContainer.id] = {
            marginMoved: false,
            paddingMoved: false
          };
        }

        // we removed width and inner align (renamed to alignText above) from the Image module
        if (module.moduleType === "Image") {
          if (!(currentId in newContainers)) {
            const container = createEntity(entities[currentId].parentId!, EntityType.Container);
            container.settings = setResponsiveValue(container.settings!, "padding", "0px");
            entities = wrapEntity(entities, currentId, () => container);

            currentId = container.id;
          }

          // re-pull
          const container = entities[currentId];
          processPossiblyResponsiveValue(entity.settings!, (x, s) => {
            const hAlign = x.alignText;
            if (hAlign) {
              const align = hAlign === "right" ? "flex-end" : hAlign === "center" ? "center" : "flex-start";
              container.settings = setValue(container.settings!, "align", align, s);
            }
          });

          entity.settings = removeSetting(entity.settings!, "alignText");
          entity.settings = removeSetting(entity.settings, "width");
        }

        break;
      }
      case EntityType.Container: {
        let needsWrap = false;
        processPossiblyResponsiveValue(entity.settings!, (x, s) => {
          needsWrap ||= x.containerWidth !== "100%" && x.containerWidth !== "auto";

          if (x.outerWidth === "100%" || x.outerWidth === "auto") {
            x.width = "fill";
          } else {
            x.width = x.outerWidth;
            x.maxWidth = "100%";
          }
        });

        if (needsWrap) {
          const wrap = createEntity(entity.parentId!, EntityType.Container);
          wrap.settings = setResponsiveValue(wrap.settings!, "padding", "0px");
          entities = wrapEntity(entities, entity.id, () => {
            processPossiblyResponsiveValue(entity.settings!, (x, s) => {
              if (x.horizontalAlign) {
                const align =
                  x.horizontalAlign === "right" ? "flex-end" : x.horizontalAlign === "center" ? "center" : "flex-start";
                wrap.settings = setValue(wrap.settings!, "align", align, s);
              }
            });
            return wrap;
          });

          newContainers[wrap.id] = {
            marginMoved: false,
            paddingMoved: false
          };
        }
        break;
      }
    }
  });

  // upgrade buttons
  if (
    Object.values(entities).some((x) => {
      if (x.type === EntityType.ModuleItem) {
        const parent = entities[x.parentId!] as ModuleEntity;
        return parent.moduleType === "Button";
      }
    })
  ) {
    const buttons = Object.values(entities).filter((x) => isModuleEntity(x) && x.moduleType === "Button");
    for (const button of buttons) {
      let container: Entity;
      if (newContainers[button.parentId!]) {
        container = entities[button.parentId!];
      } else {
        container = createEntity(button.parentId!, EntityType.Container);
        entities = dropEntity(entities, container, {
          id: button.id,
          isAfter: true
        });
      }

      processPossiblyResponsiveValue(button.settings!, (x, screen) => {
        container.settings = setValue(container.settings!, "gap", x.gap, screen);
        container.settings = setValue(
          container.settings!,
          "justify",
          innerHorizontalAlignToJustifyContent(x.innerHorizontalAlign),
          screen
        );
        container.settings = setValue(
          container.settings!,
          "direction",
          x.inlineButton === false && button.childrenIds!.length > 1 ? "column" : "row",
          screen
        );
        const info = newContainers[container.id] ?? {
          marginMoved: false,
          paddingMoved: false
        };
        if (!info.marginMoved) {
          container.settings = setValue(container.settings!, "margin", x.margin, screen);
        }
        if (!info.paddingMoved) {
          container.settings = setValue(container.settings!, "padding", x.padding, screen);
        }
      });

      // process children
      const children = button.childrenIds!.map((x) => entities[x]);
      for (const child of children) {
        entities = deleteEntity(entities, child.id);
      }

      entities = deleteEntity(entities, button.id);
      for (let i = 0; i < children.length; i++) {
        const newButton = createModule(container.id, "Button");
        newButton.settings = children[i].settings;
        processPossiblyResponsiveValue(button.settings!, (x, screen) => {
          newButton.settings = setValue(
            newButton.settings!,
            "minWidth",
            x.inlineButton === false ? x.containerWidth : undefined,
            screen
          );
        });
        entities = addEntity(entities, newButton);
      }
    }
  }

  // remove missing entities
  Object.values(entities).forEach((x) => {
    if (x.childrenIds) {
      x.childrenIds = x.childrenIds.filter((y) => y in entities);
    }
  });

  // 1. wrap block children into a container and set the width
  // 2. if padding/margin has % value, wrap children into container and move the value
  Object.values(entities).forEach((entity) => {
    if (!entity.settings || compareVersionNumbers(entity.settings.version, version) >= 0) return;

    switch (entity.type) {
      case EntityType.Block: {
        entities = wrapChildren(entities, entity.id, () => {
          const container = createEntity(entity.id, EntityType.Container);
          container.settings = setResponsiveValue(container.settings!, "width", "fill");
          container.settings = setResponsiveValue(container.settings!, "maxWidth", "100%");
          processPossiblyResponsiveValue(entity.settings!, (x, s) => {
            container.settings = setValue(container.settings!, "padding", x.padding, s);
            container.settings = setValue(container.settings!, "width", x.innerWidth, s);
          });
          entity.settings = setResponsiveValue(entity.settings!, "padding", "0px");
          entity.settings = removeSetting(entity.settings, "innerWidth");
          return container;
        });

        break;
      }
      case EntityType.Row:
      case EntityType.Column: {
        let hasPercent = false;
        processPossiblyResponsiveValue(entity.settings!, (x, s) => {
          hasPercent ||= marginPaddingHasPercent(x.margin) || marginPaddingHasPercent(x.padding);
        });

        if (hasPercent) {
          const wrapFn = entity.type === EntityType.Row ? wrapEntity : wrapChildren;
          entities = wrapFn(entities, entity.id, () => {
            const container =
              entity.parentId && newContainers[entity.parentId]
                ? entities[entity.parentId]
                : createEntity(entity.id, EntityType.Container);
            processPossiblyResponsiveValue(entity.settings!, (x, s) => {
              container.settings = setValue(container.settings!, "background", x.background, s);
              container.settings = setValue(container.settings!, "border", x.border, s);
              container.settings = setValue(container.settings!, "shadow", x.shadow, s);

              container.settings = setValue(container.settings!, "margin", x.margin, s);
              container.settings = setValue(container.settings!, "padding", x.padding, s);

              if (entity.type === EntityType.Column) {
                container.settings = setValue(container.settings!, "justify", x.justify, s);
              }
            });

            if (wrapFn === wrapChildren) {
              container.settings = setResponsiveValue(container.settings!, "direction", "column");
              container.settings = setResponsiveValue(container.settings!, "height", "hug");

              if (entity.type !== EntityType.Column) {
                container.settings = setResponsiveValue(container.settings!, "justify", "center");
              }
            }

            container.settings = collapseResponsiveSettings(container.settings!);

            return container;
          });

          // after running wrapEntity, entity reference changes
          const e = entities[entity.id];

          e.settings = setResponsiveValue(e.settings!, "margin", "0px");
          e.settings = setResponsiveValue(e.settings!, "padding", "0px");
          e.settings = setResponsiveValue(e.settings!, "background", undefined);
          e.settings = setResponsiveValue(e.settings!, "border", undefined);
          e.settings = setResponsiveValue(e.settings!, "shadow", undefined);
          e.settings = collapseResponsiveSettings(e.settings!);
        }

        break;
      }
    }
  });

  if (settings) {
    processPossiblyResponsiveValue(settings, (x, screen) => {
      if (x.header?.overlap && entities.main?.childrenIds?.length) {
        const firstChild = entities[entities.main.childrenIds[0]];
        if (firstChild.settings && compareVersionNumbers(firstChild.settings.version, version) < 0) {
          const padding = getSettingValue(firstChild.settings, "", "padding", screen);
          const parsed = fromShorthand(padding, "{top} {right} {bottom} {left}");

          if (!parsed.value.top || parsed.value.top.endsWith("px")) {
            parsed.value.top = `${parseFloat(parsed.value.top) + 80}px`;

            firstChild.settings = setValue(
              firstChild.settings,
              "padding",
              toShorthand(parsed.value, "{top} {right} {bottom} {left}", "0px"),
              screen
            );
          }
        }
      }
    });
  }

  return entities;
};

export const upgradeSettings = (settings: MainSettings) => {
  let result = settings;
  if (compareVersionNumbers(settings.version, version) < 0) {
    result = upgradeBackground(settings);
  }
  return result;
};

const upgradeBackground = (settings: MainSettings): MainSettings => {
  traverseObject(settings, (obj, key) => {
    if (key !== "background" || !obj[key]) return;

    const background: CompositeSettings.Background = obj[key];
    if (background.opacity && background.opacity != "100%") {
      if (background.overlay) {
        const a = parseFloat(background.opacity);
        const b = parseFloat(background.overlay.opacity);
        background.overlay = {
          ...background.overlay,
          opacity: `${Math.max(100 - a, b)}%`
        };
      } else {
        background.overlay = {
          color: "#ffffff",
          opacity: `${100 - parseFloat(background.opacity)}%`
        };
      }
    }
  });

  return settings;
};

const marginPaddingHasPercent = (x: string | undefined | null): boolean => {
  if (!x) return false;

  const v = fromShorthand(x, "{top} {right} {bottom} {left}");
  return (
    (parseFloat(v.value.top) !== 0 && v.value.top.endsWith("%")) ||
    (parseFloat(v.value.right) !== 0 && v.value.right.endsWith("%")) ||
    (parseFloat(v.value.bottom) !== 0 && v.value.bottom.endsWith("%")) ||
    (parseFloat(v.value.left) !== 0 && v.value.left.endsWith("%"))
  );
};
